<script setup>
/**
 * Tasks — "the things your assistant can do, step by step" — and, on its second tab, the
 * MENUS that start them. THE PLAN, §4 P7; the navigation consolidation, §3.1.
 *
 * ── WHY MENUS IS A TAB HERE AND NOT A ROW OF ITS OWN ───────────────────────────────────
 *
 * The retired `nav.js` entry gave the reason itself, as an argument for sitting next to
 * this one: *"an option can START one: the two are built together."* A row whose stated
 * justification is the row above it is a tab. The sidebar went from eleven rows to nine
 * this way, and the second one is on Approved messages for the same kind of reason.
 *
 * THE TAB IS A QUERY PARAMETER (`?tab=menus`), exactly as `?step=` is on the composer and
 * `?range=` is on Activity. That is structural, not cosmetic: `SidebarGroup` resolves every
 * child's `to` through `router.resolve()` inside a computed during render, and an
 * unregistered route name throws there and blanks the ENTIRE primary navigation. A tab that
 * is a route record is a route record that can be got wrong; a tab that is a query string
 * cannot be. `waslo_menus` therefore keeps its record and redirects here.
 *
 * ── WHAT IS UNUSUAL ABOUT THE ANATOMY, AND WHY ─────────────────────────────────────────
 *
 * THE BRIEF FOR THIS PIECE NAMES ITS OWN RISK: *this is the concept most likely to be
 * misunderstood.* It is, and it is misunderstood in one specific way — as a second place to
 * write answers. Everything unusual below is that one sentence:
 *
 *  • THE EXPLANATION COMES FIRST AND STAYS. Two blocks, not one: a paragraph that opens by
 *    naming what a task is NOT ("Answers tell your assistant what to say; a task tells it
 *    what to do"), then a worked example that draws the shape — a customer sentence in,
 *    three numbered instructions, a written-down result out. See `TaskExample.vue` for why
 *    prose alone has never been enough here. The Menus tab keeps its own pair.
 *
 *  • THE EMPTY STATE IS INLINE, and `WasloPageLayout`'s `isEmpty` branch is deliberately
 *    NOT used. That branch replaces the body slot, which would delete the explanation at
 *    the exact moment it is the entire content of the screen — an owner with no tasks is
 *    the only person guaranteed not to know what one is.
 *
 *  • THE PRIMARY BUTTON IS IN THE PAGE HEADER, and it FOLLOWS THE TAB: "New task" on one,
 *    "New menu" on the other. A header button that still said "New task" while an owner was
 *    looking at her menus would be a button that lies about where it goes. It is suppressed
 *    on an empty list on both tabs, because the inline empty-state card carries the action
 *    there instead — which is what Answers does too.
 *
 * ── THE MENUS READ IS LAZY, AND THE FRAME FOLLOWS THE TAB ──────────────────────────────
 *
 * `immediate: false`, loaded the first time the tab is opened. An owner who came to switch
 * a task off should not pay for a list she is not looking at; an owner who opens the tab
 * twice should not pay twice. And the four states `WasloPageLayout` renders are the ACTIVE
 * tab's, never a mix of both — a spinner over one list while the other has already failed is
 * a frame that cannot be read. The tab bar itself lives in `#controls`, which the frame
 * renders above every state branch, so it stays usable while the body is loading or broken.
 *
 * The on/off switch on each row writes on its own, with `{ enabled }` and nothing else —
 * see `TaskCard.vue` for why that is safe and why it matters.
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import MenusTab from '../components/MenusTab.vue';
import RecentlyRemoved from '../components/RecentlyRemoved.vue';
import TaskCard from '../components/TaskCard.vue';
import TaskExample from '../components/TaskExample.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { wasloApi } from '../api/client.js';
import { accountChannels } from '../api/channels.js';
import { draftedSets } from '../helpers/claude.js';

const { t } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

// ── which tab ──────────────────────────────────────────────────────────────────────
// The two values are written out rather than derived, and an unknown one falls back to the
// first: `?tab=` is in bookmarks and in support replies, and a value this build has never
// heard of must open the page, not a blank area.
const TABS = ['tasks', 'menus'];

const tab = computed(() => {
  const asked = String(route?.query?.tab ?? '');
  return TABS.includes(asked) ? asked : TABS[0];
});

const tabs = computed(() => [
  { value: 'tasks', label: t('WASLO.NAV.TASKS') },
  { value: 'menus', label: t('WASLO.NAV.MENUS') },
]);

const activeTabIndex = computed(() => TABS.indexOf(tab.value));

// `replace`, not `push` — the same decision Activity's period control records: a tab is a
// view of one screen rather than a place you visited, and pushing it makes Back walk through
// every tab the owner tried instead of leaving the section.
const selectTab = chosen => {
  if (!chosen || chosen.value === tab.value) return;
  router.replace({ query: { ...(route?.query ?? {}), tab: chosen.value } });
};

// ── the two lists ──────────────────────────────────────────────────────────────────
// `isEmpty: () => false` on both. An empty list is a MEANING ("you have not written one
// yet"), not the absence of data, and it is rendered as one — under the explanation, which
// the composable's EMPTY state would have replaced.
const tasks = useWasloApi(
  signal => wasloApi.getTasks(accountId.value, signal),
  { isEmpty: () => false }
);

const menus = useWasloApi(
  signal => wasloApi.getMenus(accountId.value, signal),
  { isEmpty: () => false, immediate: false }
);

/**
 * P-10.26: the account's channels, read from Chatwoot, so a task limited to some of them can say
 * which it is not on. Supporting: a failed read is `[]` and the rows say nothing.
 */
const accountChannelList = useWasloApi(
  signal => accountChannels(accountId.value, signal),
  { isEmpty: () => false }
);

/** E2 (P-8.4): what Claude drafted that nobody has touched. Supporting: a failed read shows no chip. */
const claudeSummary = useWasloApi(
  signal => wasloApi.getAssistantSummary(accountId.value, signal),
  { isEmpty: () => false }
);
const drafted = computed(() => draftedSets(claudeSummary.data.value));
const draftedMenus = computed(() => [...drafted.value.menus]);

/**
 * Has the Menus tab ever been opened?
 *
 * It does two things, and they are both about not answering a question nobody asked. It
 * triggers the one read (`immediate: false` above), and it keeps the tab out of the DOM
 * until then — otherwise a hidden `MenusTab` would render "You have no menus yet" from an
 * empty array it has never fetched, which is a confident answer derived from an absence and
 * the exact failure this section is written against. Once opened it stays mounted, so
 * switching back and forth costs nothing and loses no scroll position.
 */
const menusOpened = ref(false);

watch(
  tab,
  current => {
    if (current !== 'menus' || menusOpened.value) return;
    menusOpened.value = true;
    menus.reload();
  },
  { immediate: true }
);

const rows = computed(() => tasks.data.value ?? []);
const menuRows = computed(() => menus.data.value ?? []);

const onMenusTab = computed(() => tab.value === 'menus');

// ── the frame, which is always the ACTIVE tab's ────────────────────────────────────
const isFetching = computed(() =>
  onMenusTab.value ? menus.isFetching.value : tasks.isFetching.value
);
const isNotProvisioned = computed(() =>
  onMenusTab.value ? menus.isNotProvisioned.value : tasks.isNotProvisioned.value
);
const errorMessage = computed(() =>
  onMenusTab.value ? menus.errorMessage.value : tasks.errorMessage.value
);

const buttonLabel = computed(() => {
  if (onMenusTab.value) {
    return menuRows.value.length ? t('WASLO.MENUS.ADD') : '';
  }
  return rows.value.length ? t('WASLO.TASKS.ADD') : '';
});

const newUrl = computed(() => {
  if (accountId.value === null) return null;
  return frontendURL(
    `accounts/${accountId.value}/waslo/${onMenusTab.value ? 'menus' : 'tasks'}/new`
  );
});

const openNew = () => {
  if (newUrl.value) router.push(newUrl.value);
};

const retry = () => (onMenusTab.value ? menus.reload() : tasks.reload());

// ── the row switches ───────────────────────────────────────────────────────────────
// One id at a time, not one boolean for the page: two rows toggled in quick succession
// must not make each other's row look busy, and a failure on one must not remount the
// other's control. Written once and used by both lists, because a second copy of this is a
// second place for the `switchEpoch` remount trick to be forgotten.
const switching = () => ({
  savingId: ref(null),
  saveError: ref(''),
  switchEpoch: ref(0),
});

const taskSwitch = switching();
const menuSwitch = switching();

const runToggle = async (state, list, write, failed, row, next) => {
  if (state.savingId.value !== null) return;
  state.savingId.value = row.id;
  state.saveError.value = '';
  try {
    // Render from the response, never from what we sent: the BFF echoes the saved row.
    const saved = await write(accountId.value, row.id, { enabled: next });
    list.data.value = (list.data.value ?? []).map(one =>
      one.id === saved.id ? saved : one
    );
  } catch {
    state.saveError.value = t(failed);
    // `Switch` flips its own copy on click whether or not we agreed. Remounting every row's
    // control is the one reliable way to snap the failed one back to what the server says.
    state.switchEpoch.value += 1;
  } finally {
    state.savingId.value = null;
  }
};

const onToggleTask = (task, next) =>
  runToggle(
    taskSwitch,
    tasks,
    wasloApi.updateTask,
    'WASLO.TASKS.LIST.SAVE_FAILED',
    task,
    next
  );

// ── F3.6: a task removed from its row ───────────────────────────────────────────────
// The row goes from the list at once, and "Recently removed" below re-reads so the way back is
// on screen straight away.
const removedTasks = ref(null);
const onTaskRemoved = taskId => {
  tasks.data.value = (tasks.data.value ?? []).filter(one => one.id !== taskId);
  useAlert(t('WASLO.TASKS.REMOVE.REMOVED'));
  removedTasks.value?.reload();
};

const onToggleMenu = (menu, next) =>
  runToggle(
    menuSwitch,
    menus,
    wasloApi.updateMenu,
    'WASLO.MENUS.LIST.SAVE_FAILED',
    menu,
    next
  );
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.TASKS.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="isNotProvisioned"
    :error-message="errorMessage"
    :button-label="buttonLabel"
    @click="openNew"
    @retry="retry"
  >
    <!--
      Outside the state branches on purpose, the same place Activity puts its period
      control: the tab you just pressed must not disappear while the list under it loads,
      and the tab you did NOT press must stay reachable when the one you did has failed.
    -->
    <template #controls>
      <div class="flex flex-wrap items-center gap-3 pb-4">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          :aria-label="t('WASLO.TASKS.TABS.LABEL')"
          data-waslo-tasks-tabs
          @tab-changed="selectTab"
        />
      </div>
    </template>

    <template #body>
      <div v-show="!onMenusTab" class="flex flex-col gap-8 pb-10">
        <!--
          1 · WHAT A TASK IS, before anything that assumes you know.

          Side by side from `lg`, and that is a real decision rather than a layout
          preference: stacked, the two teaching blocks are about 850px tall, so an owner
          who already has four tasks scrolls a full viewport past an explanation she has
          read forty times to reach her own list. Two columns puts the explanation AND the
          first rows on the same screen, which is the only version that serves both the
          first visit and the fortieth. Below `lg` they stack, explanation first.
        -->
        <div class="grid gap-4 lg:grid-cols-2">
          <WasloExplainer
            icon="i-lucide-list-checks"
            :title="t('WASLO.TASKS.SPOTLIGHT_TITLE')"
            :note="t('WASLO.TASKS.SPOTLIGHT')"
          />
          <TaskExample />
        </div>

        <!-- 2 · THE TASKS THEMSELVES. -->
        <section class="flex flex-col gap-4" data-waslo-tasks>
          <SettingsHeader
            :heading="t('WASLO.TASKS.LIST.HEADING')"
            :description="t('WASLO.TASKS.LIST.DESCRIPTION')"
          />

          <Banner
            v-if="taskSwitch.saveError.value"
            color="ruby"
            data-waslo-tasks-error
          >
            {{ taskSwitch.saveError.value }}
          </Banner>

          <ul
            v-if="rows.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-task-list
          >
            <TaskCard
              v-for="task in rows"
              :key="task.id"
              :task="task"
              :account-id="accountId"
              :is-saving="taskSwitch.savingId.value === task.id"
              :switch-epoch="taskSwitch.switchEpoch.value"
              :account-channels="accountChannelList.data.value ?? []"
              :suggested-by-claude="drafted.tasks.has(task.id)"
              @toggle="next => onToggleTask(task, next)"
              @removed="onTaskRemoved"
            />
          </ul>

          <!--
            The empty state, inline and under the explanation rather than instead of it.
            Solid card, no dashed border (that is dropzone convention), no illustration —
            the same anatomy as every other card in the section, because "you have not
            written one yet" is content, not a failure.
          -->
          <div
            v-else
            class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-tasks-empty
          >
            <span
              class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-blue-3"
              aria-hidden="true"
            >
              <span class="size-5 i-lucide-list-plus text-n-blue-10" />
            </span>
            <div class="flex flex-col items-start gap-3">
              <div class="flex flex-col gap-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('WASLO.TASKS.EMPTY.TITLE') }}
                </p>
                <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                  {{ t('WASLO.TASKS.EMPTY.BODY') }}
                </p>
              </div>
              <Button
                :label="t('WASLO.TASKS.EMPTY.ACTION')"
                icon="i-lucide-plus"
                size="sm"
                data-waslo-task-add-first
                @click="openNew"
              />
            </div>
          </div>
        </section>

        <!-- P-6.11: tasks removed in the last 30 days. Hidden until one was. -->
        <RecentlyRemoved
          ref="removedTasks"
          :account-id="accountId"
          :kinds="['task']"
          @restored="tasks.reload()"
        />
      </div>

      <!-- `v-if` until it has been opened once, then `v-show` — see `menusOpened`. -->
      <div v-if="menusOpened" v-show="onMenusTab" class="flex flex-col gap-8">
        <MenusTab
          :rows="menuRows"
          :account-id="accountId"
          :saving-id="menuSwitch.savingId.value"
          :switch-epoch="menuSwitch.switchEpoch.value"
          :save-error="menuSwitch.saveError.value"
          :claude-drafted="draftedMenus"
          @toggle="onToggleMenu"
          @add="openNew"
        />
        <RecentlyRemoved
          :account-id="accountId"
          :kinds="['menu']"
          @restored="menus.reload()"
        />
      </div>
    </template>
  </WasloPageLayout>
</template>
